import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Target, Plus, Pencil, MagnifyingGlass, Buildings, MapPin, User,
  CalendarBlank, Tag, ArrowRight, Trash, LinkSimple, Clock, Eye, CaretDown, CaretUp, Phone
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CallButton from "@/components/CallButton";
import Pagination from "@/components/Pagination";
import CreatorSelect from "@/components/CreatorSelect";

const STATUS_LIST = ["new", "needs_review", "qualified", "needs_research", "assigned", "rejected", "duplicate", "existing_company", "contacted", "converted", "lost"];
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800",
  needs_review: "bg-orange-100 text-orange-800",
  qualified: "bg-emerald-100 text-emerald-800",
  needs_research: "bg-amber-100 text-amber-800",
  assigned: "bg-indigo-100 text-indigo-800",
  rejected: "bg-red-100 text-red-800",
  duplicate: "bg-gray-100 text-gray-600",
  existing_company: "bg-teal-100 text-teal-800",
  contacted: "bg-yellow-100 text-yellow-800",
  converted: "bg-green-100 text-green-800",
  lost: "bg-red-100 text-red-800",
};

const SECTORS = [
  "Healthcare & Social Care", "IT & Technology", "Accounting & Finance",
  "Education & Childcare", "Commercial Sales & Business Development",
];

const EMPTY_FORM = { created_by: "",
  company_name: "", opportunity: "", sector: "", location: "", phone: "",
  source: "Manual", priority_score: 50, status: "new", notes: "",
  assigned_to: "", company_id: "", lead_type: "company",
};

function formatStatus(s) {
  return (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function LeadsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isTL = user?.role === "team_leader";
  const canManage = isAdmin || isTL;

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkCompanyId, setLinkCompanyId] = useState("");
  const [hoveredLead, setHoveredLead] = useState(null);
  const [page, setPage] = useState(1);

  const loadLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (sectorFilter && sectorFilter !== "all") params.set("sector", sectorFilter);
      params.set("page", page);
      params.set("limit", 25);
      const { data } = await api.get(`/leads?${params}`);
      setLeads(data.items || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, statusFilter, sectorFilter, page]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  useEffect(() => {
    if (canManage) {
      api.get("/leads/team/members").then(r => setTeamMembers(r.data || [])).catch(() => {});
    }
  }, [canManage]);

  const openAddForm = () => {
    setEditingLead(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEditForm = (l) => {
    setEditingLead(l);
    setForm({
      company_name: l.company_name || "", opportunity: l.opportunity || "",
      sector: l.sector || "", location: l.location || "", phone: l.phone || "",
      source: l.source || "Manual", priority_score: l.priority_score || 50,
      status: l.status || "new", notes: l.notes || "",
      assigned_to: l.assigned_to || "", company_id: l.company_id || "",
      lead_type: l.lead_type || "company",
    });
    setShowPreview(false);
    setShowForm(true);
  };

  const openPreview = (l) => {
    setSelectedLead(l);
    setShowPreview(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingLead) {
        await api.put(`/leads/${editingLead.id}`, form);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", form);
        toast.success("Lead created");
      }
      setShowForm(false);
      loadLeads();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save lead"); }
    finally { setSaving(false); }
  };

  const quickStatusUpdate = async (id, status) => {
    try {
      await api.put(`/leads/${id}`, { status });
      toast.success("Status updated");
      loadLeads();
      if (selectedLead?.id === id) setSelectedLead({ ...selectedLead, status });
    } catch { toast.error("Update failed"); }
  };

  const handleAssign = async (leadId, userId) => {
    try {
      await api.put(`/leads/${leadId}`, { assigned_to: userId, status: userId ? "assigned" : "new" });
      toast.success("Lead assigned");
      // Refresh selected lead
      const { data } = await api.get(`/leads/${leadId}`);
      setSelectedLead(data);
      loadLeads();
    } catch { toast.error("Assignment failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      setShowPreview(false);
      loadLeads();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const linkToCompany = async () => {
    if (!linkCompanyId || !selectedLead) return;
    try {
      await api.post(`/leads/${selectedLead.id}/link-company`, { company_id: linkCompanyId });
      toast.success("Lead linked to company");
      setShowLinkDialog(false);
      // Refresh selected lead
      const { data } = await api.get(`/leads/${selectedLead.id}`);
      setSelectedLead(data);
      loadLeads();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to link"); }
  };

  const loadCompanies = async () => {
    try {
      const { data } = await api.get("/companies?limit=200");
      setCompanies(data.items || []);
    } catch { /* ignore */ }
  };

  const getPriorityColor = (s) => s >= 70 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : s >= 40 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200";

  return (
    <div data-testid="leads-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">Leads</h1>
          <p className="text-sm text-[#6B7280]">{total} lead{total !== 1 ? "s" : ""} in pipeline</p>
        </div>
        <Button onClick={openAddForm} className="gap-2 bg-[#0F172A]" data-testid="add-lead-btn">
          <Plus size={16} /> New Lead
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            placeholder="Search leads by company, opportunity, source..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 border-[#E5E7EB]"
            data-testid="lead-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-10 border-[#E5E7EB]" data-testid="lead-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUS_LIST.map(s => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sectorFilter} onValueChange={setSectorFilter}>
          <SelectTrigger className="w-[180px] h-10 border-[#E5E7EB]" data-testid="lead-sector-filter">
            <SelectValue placeholder="All Sectors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sectors</SelectItem>
            {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <th className="text-left py-3 px-4 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide">Company</th>
                <th className="text-left py-3 px-4 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide hidden md:table-cell">Opportunity</th>
                <th className="text-left py-3 px-3 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide hidden lg:table-cell">Sector</th>
                <th className="text-left py-3 px-3 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide hidden lg:table-cell">Submitted By</th>
                <th className="text-left py-3 px-3 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide hidden xl:table-cell">Assigned To</th>
                <th className="text-left py-3 px-3 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide">Status</th>
                <th className="text-left py-3 px-3 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide hidden md:table-cell">Source</th>
                <th className="text-left py-3 px-3 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide hidden xl:table-cell">Priority</th>
                <th className="text-left py-3 px-3 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide hidden lg:table-cell">Date</th>
                <th className="text-right py-3 px-4 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [1,2,3].map(i => <tr key={i}><td colSpan={10}><div className="h-12 skeleton-shimmer rounded m-2" /></td></tr>)
              ) : leads.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-[#6B7280]">
                  <Target size={32} className="mx-auto mb-2 text-[#D1D5DB]" />
                  <p className="font-medium">No leads found</p>
                  <p className="text-xs mt-1">Create your first lead or adjust your filters</p>
                </td></tr>
              ) : leads.map(l => (
                <tr
                  key={l.id}
                  className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors cursor-pointer"
                  onClick={() => openPreview(l)}
                  onMouseEnter={() => setHoveredLead(l.id)}
                  onMouseLeave={() => setHoveredLead(null)}
                  data-testid={`lead-row-${l.id}`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                        <Buildings size={14} className="text-[#C5A059]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#0F172A] truncate text-sm">{l.company_name || "Unnamed"}</p>
                        <p className="text-[10px] text-[#9CA3AF] truncate md:hidden">{l.opportunity || ""}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell">
                    <p className="text-[#374151] truncate max-w-[200px]">{l.opportunity || "-"}</p>
                  </td>
                  <td className="py-3 px-3 hidden lg:table-cell">
                    <span className="text-xs text-[#6B7280]">{l.sector || "-"}</span>
                  </td>
                  <td className="py-3 px-3 hidden lg:table-cell">
                    <span className="text-xs text-[#374151] font-medium">{l.submitted_by_name || "-"}</span>
                  </td>
                  <td className="py-3 px-3 hidden xl:table-cell">
                    <span className="text-xs text-[#374151]">{l.assigned_to_name || "Unassigned"}</span>
                  </td>
                  <td className="py-3 px-3">
                    <Badge className={`text-[10px] ${STATUS_COLORS[l.status] || "bg-gray-100 text-gray-600"}`}>
                      {formatStatus(l.status)}
                    </Badge>
                  </td>
                  <td className="py-3 px-3 hidden md:table-cell">
                    <span className="text-xs text-[#6B7280]">{l.source || "-"}</span>
                  </td>
                  <td className="py-3 px-3 hidden xl:table-cell">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold border ${getPriorityColor(l.priority_score)}`}>
                      {l.priority_score}
                    </span>
                  </td>
                  <td className="py-3 px-3 hidden lg:table-cell">
                    <span className="text-[10px] text-[#9CA3AF]">{timeAgo(l.created_at)}</span>
                  </td>
                  <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openPreview(l)} className="text-xs text-[#0F172A]" data-testid={`view-lead-${l.id}`}>
                      <Eye size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} limit={25} onPageChange={(p) => { setPage(p); setLoading(true); }} />
      </div>

      {/* ==================== LEAD PREVIEW/DETAIL DIALOG ==================== */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] text-lg flex items-center gap-2">
              <Buildings size={20} weight="duotone" className="text-[#C5A059]" />
              {selectedLead?.company_name || "Lead Details"}
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4 mt-2">
              {/* Top badges */}
              <div className="flex flex-wrap gap-2">
                <Badge className={`text-[10px] ${STATUS_COLORS[selectedLead.status]}`}>{formatStatus(selectedLead.status)}</Badge>
                {selectedLead.sector && <Badge variant="outline" className="text-[10px]">{selectedLead.sector}</Badge>}
                {selectedLead.source && <Badge variant="outline" className="text-[10px]">{selectedLead.source}</Badge>}
              </div>

              {/* Key details grid */}
              <div className="grid grid-cols-2 gap-3">
                <PreviewField icon={Buildings} label="Company" value={selectedLead.company_name || "-"} />
                <PreviewField icon={Target} label="Opportunity" value={selectedLead.opportunity || "-"} />
                <PreviewField icon={Tag} label="Sector" value={selectedLead.sector || "-"} />
                <PreviewField icon={MapPin} label="Location" value={selectedLead.location || "-"} />
                <PreviewField icon={ArrowRight} label="Source" value={selectedLead.source || "-"} />
                <PreviewField icon={User} label="Submitted By" value={selectedLead.submitted_by_name || "-"} />
                <PreviewField icon={User} label="Assigned To" value={selectedLead.assigned_to_name || "Unassigned"} />
                <PreviewField icon={CalendarBlank} label="Submitted" value={selectedLead.created_at ? new Date(selectedLead.created_at).toLocaleDateString("en-GB") : "-"} />
              </div>

              {/* Phone with Call Button */}
              {selectedLead.phone && (
                <div>
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Phone</p>
                  <CallButton phone={selectedLead.phone} label="Contact" />
                </div>
              )}

              {selectedLead.notes && (
                <div>
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Notes</p>
                  <div className="p-3 bg-[#FFFBEB] rounded-lg border border-amber-200 text-sm text-[#374151] whitespace-pre-wrap">{selectedLead.notes}</div>
                </div>
              )}

              {/* Quick Status Change */}
              {canManage && (
                <div>
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Change Status</p>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUS_LIST.slice(0, 8).map(s => (
                      <Button key={s} size="sm" variant={selectedLead.status === s ? "default" : "outline"}
                        className={`text-[10px] h-7 ${selectedLead.status === s ? "bg-[#0F172A] text-white" : "border-[#E5E7EB] text-[#6B7280]"}`}
                        onClick={() => quickStatusUpdate(selectedLead.id, s)} data-testid={`status-btn-${s}`}>
                        {formatStatus(s)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Assign to recruiter */}
              {canManage && (
                <div>
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Assign To Recruiter</p>
                  <Select value={selectedLead.assigned_to || "unassigned"} onValueChange={(v) => handleAssign(selectedLead.id, v === "unassigned" ? "" : v)}>
                    <SelectTrigger className="h-9 border-[#E5E7EB]" data-testid="assign-lead-select">
                      <SelectValue placeholder="Select recruiter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.role?.replace(/_/g, " ")})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Separator />

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => openEditForm(selectedLead)} className="gap-1.5 bg-[#0F172A] text-xs" data-testid="edit-lead-btn">
                  <Pencil size={14} /> Edit
                </Button>
                {canManage && (
                  <Button variant="outline" className="gap-1.5 text-xs border-[#1E3A8A] text-[#1E3A8A]" onClick={() => { loadCompanies(); setShowLinkDialog(true); }} data-testid="link-company-btn">
                    <LinkSimple size={14} /> Link to Company
                  </Button>
                )}
                {canManage && (
                  <Button variant="outline" onClick={() => handleDelete(selectedLead.id)} className="gap-1 border-[#EF4444] text-[#EF4444] hover:bg-red-50 ml-auto text-xs" data-testid="delete-lead-btn">
                    <Trash size={14} /> Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== LINK TO COMPANY DIALOG ==================== */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-[#0F172A]">Link to Existing Company</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={linkCompanyId} onValueChange={setLinkCompanyId}>
              <SelectTrigger className="h-10 border-[#E5E7EB]" data-testid="link-company-select">
                <SelectValue placeholder="Select a company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
              <Button onClick={linkToCompany} disabled={!linkCompanyId} className="bg-[#0F172A]" data-testid="confirm-link-btn">Link Company</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== ADD / EDIT DIALOG ==================== */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] flex items-center gap-2">
              <Target size={20} weight="duotone" className="text-[#10B981]" />
              {editingLead ? "Edit Lead" : "New Lead"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs">Company Name *</Label>
                <Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="e.g. ABC Healthcare" className="h-10 border-[#E5E7EB]" data-testid="lead-company-name-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Opportunity / Description</Label>
                <Input value={form.opportunity} onChange={e => setForm({...form, opportunity: e.target.value})} placeholder="e.g. 8 Care Manager vacancies" className="h-10 border-[#E5E7EB]" data-testid="lead-opportunity-input" />
              </div>
              <div>
                <Label className="text-xs">Sector</Label>
                <Select value={form.sector} onValueChange={v => setForm({...form, sector: v})}>
                  <SelectTrigger className="h-10 border-[#E5E7EB]"><SelectValue placeholder="Select sector" /></SelectTrigger>
                  <SelectContent>
                    {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g. Liverpool" className="h-10 border-[#E5E7EB]" data-testid="lead-location-input" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+44..." className="h-10 border-[#E5E7EB]" data-testid="lead-phone-input" />
              </div>
              <div>
                <Label className="text-xs">Source</Label>
                <Input value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="e.g. LinkedIn, Referral" className="h-10 border-[#E5E7EB]" data-testid="lead-source-input" />
              </div>
              <div>
                <Label className="text-xs">Priority (0-100)</Label>
                <Input type="number" min={0} max={100} value={form.priority_score} onChange={e => setForm({...form, priority_score: parseInt(e.target.value) || 0})} className="h-10 border-[#E5E7EB]" data-testid="lead-priority-input" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="h-10 border-[#E5E7EB]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_LIST.map(s => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {canManage && (
                <div>
                  <Label className="text-xs">Assign To</Label>
                  <Select value={form.assigned_to || "unassigned"} onValueChange={v => setForm({...form, assigned_to: v === "unassigned" ? "" : v})}>
                    <SelectTrigger className="h-10 border-[#E5E7EB]"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} placeholder="Details about this opportunity..." className="border-[#E5E7EB]" data-testid="lead-notes-input" />
            </div>
            {!editingLead && (
              <CreatorSelect value={form.created_by} onChange={v => setForm({...form, created_by: v})} testId="lead-creator-select" />
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0F172A]" data-testid="submit-lead-btn">
                {saving ? "Saving..." : editingLead ? "Update Lead" : "Create Lead"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 p-2.5 bg-[#F9FAFB] rounded-lg">
      <div className="w-7 h-7 rounded-md bg-white border border-[#E5E7EB] flex items-center justify-center flex-shrink-0">
        <Icon size={14} weight="duotone" className="text-[#6B7280]" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide font-medium">{label}</p>
        <p className="text-xs text-[#0F172A] font-medium truncate">{value}</p>
      </div>
    </div>
  );
}
